import type { HostToolResult } from 'models'
import { PageAccessLevel, pageAccessAllows } from 'models'
import type { PageAccessCapability } from 'models'
import {
    PAGE_NAVIGATION_KEY,
    type HostToolConfirmation,
    type HostToolDeferredResult,
    type RegisteredHostTool,
} from 'composables'
import {
    PAGE_KEYS,
    bringIntoView,
    cancelDialogOf,
    click,
    defaultButtonOf,
    fill,
    formSubmittedBy,
    formSubmittedByEnter,
    holdSubmits,
    hover,
    leavesDocument,
    leavesSite,
    press,
    submitByEnter,
} from './actions'
import {
    PageRefs,
    blockingDialog,
    confirmationQuestion,
    describe,
    fieldTitle,
    filteredText,
    formSummary,
    formValues,
    isExcluded,
    isHidden,
    nameOf,
    openListOf,
    openModal,
    roleOf,
    selectorsAreValid,
    snapshot,
    type PageRules,
} from './snapshot'
import { listenForAnnouncements } from './announcements'
import {
    canHoldLeaving,
    holdLeaving,
    pageChangeSettled,
    pageQuiet,
    routeTo,
    startPageChange,
    type LeavingHold,
} from './navigation'

/**
 * Under the 32k-character cap of a host result, counted on its serialized
 * form: JSON escaping makes the text longer, and an over-long result is
 * refused, not shortened.
 */
const SNAPSHOT_MAX_CHARS = 24_000
const REGION_MAX_CHARS = 2_000
/** What a result reads of a page the action routed to: its main part, enough for most next steps. */
const ROUTED_MAX_CHARS = 8_000
const PAGE_TOOL_TIMEOUT_MS = 15_000
/** What the site may still do after an action before the result reads the page again. */
const SETTLE_MS = 400
/** How much of the page `page_find` searches, past what a snapshot shows. */
const FIND_SCAN_MAX_CHARS = 400_000
const FIND_MAX_CHARS = 4_000
/** The lines `page_find` shows around a match: one before, three after. */
const FIND_BEFORE = 1
const FIND_AFTER = 3
/** Under the tool timeout, with room for the answer. */
const WAIT_MAX_SECONDS = 10
/** The fields one `page_fill` writes at most (spec `page-tools-improvements.md`, M4). */
const FILL_MAX_FIELDS = 20
const WAIT_POLL_MS = 500
/** How long a submit waits for the page to announce its outcome, past the settle. */
const ANNOUNCE_WAIT_MS = 2_000
const ANNOUNCE_POLL_MS = 100
/** How long a list a field opened may take to show its options: one the site fetches as the user types. */
const LIST_WAIT_MS = 1_500
/** How far up from a field its own box goes, where a site shows what was chosen. */
const FIELD_BOX_DEPTH = 4
/** What takes input: a box with two of these is no longer one field's. */
const FIELD_CONTROLS =
    'input:not([type=hidden]), select, textarea, button, [role=combobox], [role=textbox], [contenteditable=""], [contenteditable=true]'

/** A text as a choice compares it: case, accents and runs of spaces aside. */
const comparable = (value: string): string =>
    value
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase()

const text = (value: string): HostToolResult => ({
    content: [{ type: 'text', text: value }],
})

const settle = () => new Promise((resolve) => setTimeout(resolve, SETTLE_MS))

/** An address as the model wrote it: the path on this site, the whole address elsewhere. */
/**
 * The hash of an address when it is a route (`#/admin`, `#!/admin`): in an
 * app with a hash router it is the page, not a place in it.
 */
const routeOf = (url: URL): string => (/^#!?\//.test(url.hash) ? url.hash : '')

const shortAddress = (address: string): string => {
    try {
        const url = new URL(address, location.href)
        return url.origin === location.origin
            ? `${url.pathname}${url.search}${routeOf(url)}`
            : url.href
    } catch {
        return address
    }
}

/** Marks the next page as the one where the answer goes on, and waits for it. */
const leaveForNextPage = () => {
    try {
        sessionStorage.setItem(PAGE_NAVIGATION_KEY, String(Date.now()))
    } catch {
        // Without storage the new page cannot pick the answer up: it
        // waits for the next message.
    }
    startPageChange()
}

/**
 * True when the entry before this one is of this origin: the Navigation API
 * lists only this origin's entries, so one before the current is the site's.
 * False where it cannot tell.
 */
const previousEntryIsOwn = (): boolean => {
    // Not in every browser yet, nor in every DOM typing
    const { navigation } = window as unknown as {
        navigation?: { currentEntry?: { index: number } | null }
    }
    return (navigation?.currentEntry?.index ?? 0) > 0
}

/**
 * A call the user stopped while it waited for the page: the runner has given
 * up on it, so nothing may change on the page after the stop.
 */
const stopIfAborted = (signal: AbortSignal) => {
    if (signal.aborted) {
        throw new Error('Stopped by the user.')
    }
}

type RefInput = {
    ref?: unknown
    scope?: unknown
    value?: unknown
    fields?: unknown
    direction?: unknown
    text?: unknown
    seconds?: unknown
    key?: unknown
    url?: unknown
}

/** The fields a `page_fill` writes: its `fields`, or its own `ref` and `value`. */
const fillEntries = (input: unknown): RefInput[] => {
    const { fields, ref, value } = (input ?? {}) as RefInput
    if (Array.isArray(fields)) {
        return fields.filter(
            (entry): entry is RefInput => !!entry && typeof entry === 'object',
        )
    }
    return ref === undefined ? [] : [{ ref, value }]
}

/**
 * An action that may unload the page answers first, and acts once the resume
 * carrying this answer has reached the backend: the page would leave before
 * the answer could.
 */
const leaving = (said: string, act: () => void) => ({
    result: text(
        `${said} The page may change; if it does, the conversation goes on on the new page, so read it with page_snapshot before doing anything else.`,
    ),
    afterOutput: () => {
        leaveForNextPage()
        act()
    },
})

/**
 * Shows the user where the agent acts (spec `page-tools-improvements.md`,
 * M12): the widget's cursor goes to the element before the action runs.
 */
export type PagePresence = {
    /** Points at the element, named as the snapshot names it; resolves once the user can see it there. */
    point: (element: Element, name: string) => Promise<void>
    /** Heads for the element the next action points at, while the page settles: no click, nothing waits. */
    toward?: (element: Element, name: string) => void
}

/**
 * A page of the site the app declares to the widget (`registerPages`, spec
 * `page-tools-improvements.md`, M6): `<id>` or `:id` in its path stands for
 * any one segment.
 */
export type DeclaredPage = { path: string; title?: string }

/** The addresses the model is shown at most, in a list. */
const KNOWN_ADDRESSES_SHOWN = 40

/**
 * An address of this site as the tools compare it: path and query without a
 * trailing slash, and the hash when it is a route.
 */
const addressKey = (address: string): string | undefined => {
    try {
        const url = new URL(address, location.href)
        if (url.origin !== location.origin) {
            return undefined
        }
        const path = url.pathname.replace(/\/+$/, '') || '/'
        return `${path}${url.search}${routeOf(url)}`
    } catch {
        return undefined
    }
}

/**
 * True when a declared path matches the path of `key`, a segment written
 * `<id>` or `:id` matching any one. The query does not count: a declared page
 * takes any.
 */
const declaredMatches = (declared: string, key: string): boolean => {
    const trim = (path: string) => path.replace(/\/+$/, '') || '/'
    const expected = trim(declared.split('?')[0] ?? '').split('/')
    const actual = trim(key.split('?')[0] ?? '').split('/')
    return (
        expected.length === actual.length &&
        expected.every(
            (segment, index) =>
                /^(<[^>]+>|:[\w-]+)$/.test(segment) ||
                segment === actual[index],
        )
    )
}

/** What the tools say to the user: their titles and the questions they ask. */
export type PageToolsTranslate = (
    key: string,
    params?: Record<string, unknown>,
) => string

/**
 * The page tools of the widget (spec `page-tools.md`), for the effective level
 * of the agent. Empty when an excluded selector does not parse: an exclusion
 * that does not apply must turn access off, not exclude less.
 */
export function createPageTools(
    capability: PageAccessCapability,
    {
        t,
        pages = () => [],
        presence,
        refs = new PageRefs(),
    }: {
        t: PageToolsTranslate
        /** The pages the app declared, read at each call. */
        pages?: () => readonly DeclaredPage[]
        /** Where the agent acts, shown before each action. */
        presence?: PagePresence
        /**
         * The refs the tools give and resolve: shared with what the user
         * points at (spec `page-point-and-choose.md`, P), so the model acts
         * on the ref a message carries.
         */
        refs?: PageRefs
    },
): RegisteredHostTool[] {
    const rules: PageRules = { excludeSelectors: capability.excludeSelectors }
    if (capability.level === PageAccessLevel.Off || !selectorsAreValid(rules)) {
        return []
    }
    /** Role and name each ref had when the model last read it. */
    const seen = new Map<number, string>()
    const signature = (element: Element) =>
        `${roleOf(element) ?? ''}|${nameOf(element, rules)}`

    /**
     * The value each field had when the agent first read it or acted in its
     * form: a submit card lists first what changed since (spec
     * `page-tools-improvements.md`, M2), so a field the agent wrote, in place
     * or through a dialog of the site, is never among the ones left out.
     * Kept past a submit, which may fail or be refused by the site: a later
     * card may list again a change already saved, never hide one.
     */
    const firstSeen = new WeakMap<Element, string>()
    const noteForms = (forms: Iterable<HTMLFormElement | null>) => {
        for (const form of forms) {
            if (!form) {
                continue
            }
            const values = formValues(
                form,
                rules,
                t('toolApproval.fieldChecked'),
                firstSeen,
            )
            for (const [field, value] of values) {
                firstSeen.set(field, value)
            }
        }
    }

    const read = (root: Element, maxChars: number) => {
        noteForms([root.closest('form'), ...root.querySelectorAll('form')])
        const result = snapshot(root, refs, rules, { maxChars })
        for (const line of result.text.split('\n')) {
            const ref = Number(/^\[(\d+)\]/.exec(line)?.[1])
            const element = ref ? refs.resolve(ref) : undefined
            if (element) {
                seen.set(ref, signature(element))
            }
        }
        return result
    }

    /** The element behind a ref, checked again right before acting on it. */
    const target = (input: RefInput): Element => {
        const ref = Number(input.ref)
        const element = Number.isInteger(ref) ? refs.resolve(ref) : undefined
        if (!element) {
            throw new Error(
                'That element is no longer on the page: call page_snapshot again.',
            )
        }
        if (isExcluded(element, rules)) {
            throw new Error('That element is off limits: the site excluded it.')
        }
        if (seen.has(ref) && seen.get(ref) !== signature(element)) {
            throw new Error('That element changed: call page_snapshot again.')
        }
        return element
    }

    /** An element an action may touch: one the user could reach too. */
    const actionTarget = (input: RefInput): Element => {
        const element = target(input)
        const dialog = blockingDialog(element)
        if (dialog) {
            const name = nameOf(dialog, rules)
            throw new Error(
                `That element is behind the open dialog${name ? ` "${name}"` : ''}: the user cannot reach it while the dialog is open. Act inside the dialog, or close it first.`,
            )
        }
        noteForms([
            (element as HTMLInputElement).form ?? element.closest('form'),
        ])
        return element
    }

    /** A short reading of the region around an element, after an action. */
    const around = (element: Element) => {
        const region =
            element.closest(
                'form, section, article, dialog, [role=dialog], main',
            ) ??
            element.parentElement ??
            document.body
        return read(region, REGION_MAX_CHARS).text
    }

    /** Sends the cursor toward the field of `entry`, when it is there to point at. */
    const headFor = (
        entry: RefInput,
        toward: NonNullable<PagePresence['toward']>,
    ) => {
        try {
            const element = actionTarget(entry)
            toward(element, fieldTitle(element, rules))
        } catch {
            // Gone or refused: the write that follows says so
        }
    }

    /** A field that may open a list of options as it is written. */
    const opensList = (element: Element) =>
        !!element.closest('[role=combobox]') ||
        element.matches('[aria-autocomplete=list], [aria-autocomplete=both]')

    /**
     * The list of options a field opened as it was written, once the list
     * shows some or after a short wait: picking one is the next step. A
     * combobox's list may also mount late, when the site fetches it.
     */
    const listOpenedBy = async (element: Element, signal: AbortSignal) => {
        const combobox = opensList(element)
        let list = openListOf(element, rules)
        for (
            let waited = 0;
            (list ? !list.querySelector('[role=option]') : combobox) &&
            waited < LIST_WAIT_MS &&
            !signal.aborted;
            waited += ANNOUNCE_POLL_MS
        ) {
            await new Promise((resolve) =>
                setTimeout(resolve, ANNOUNCE_POLL_MS),
            )
            list = openListOf(element, rules)
        }
        return list
    }

    /**
     * A field that searches as it is written, open and still empty with no
     * option yet: its options come from the text (demoqa's subjects). A model
     * that clicked it and saw none gave up on the field (2026-10-28). Not a
     * read-only one, which only picks, nor one whose text found nothing.
     */
    const opensEmpty = (element: Element) =>
        element instanceof HTMLInputElement &&
        !element.readOnly &&
        element.value === '' &&
        opensList(element) &&
        (element.closest('[role=combobox]') ?? element).getAttribute(
            'aria-expanded',
        ) === 'true' &&
        !openListOf(element, rules)?.querySelector('[role=option]')

    /**
     * A control that only picks from its list and takes no text: the select
     * triggers of Radix, Base UI and MUI. `page_fill` opens it and chooses
     * (spec `page-point-and-choose.md`, C).
     */
    const picksOnly = (element: Element) =>
        !(
            element instanceof HTMLInputElement ||
            element instanceof HTMLTextAreaElement ||
            element instanceof HTMLSelectElement ||
            (element as HTMLElement).isContentEditable
        ) &&
        (roleOf(element) === 'combobox' ||
            element.getAttribute('aria-haspopup') === 'listbox') &&
        // A button that also submits its form is page_click's, with its card
        !formSubmittedBy(element)

    /** The lists of options on screen now, to tell the one a click opens. */
    const listsShown = () =>
        new Set(
            [...document.querySelectorAll('[role=listbox]')].filter(
                (list) => !isHidden(list, { inert: false }),
            ),
        )

    /**
     * The list a control that picks only opened when clicked: the one it
     * names, or a list that showed after the click (a portal at the end of
     * the page, as MUI mounts it, often named only once open). Waits for its
     * options as {@link listOpenedBy} does.
     */
    const listShownBy = async (
        element: Element,
        before: Set<Element>,
        signal: AbortSignal,
    ) => {
        const find = () => {
            const named = openListOf(element, rules)
            if (named) {
                return named
            }
            const id = (element.getAttribute('aria-controls') ?? '').split(
                ' ',
            )[0]
            const controlled =
                id && element.getAttribute('aria-expanded') === 'true'
                    ? (
                          element.getRootNode() as Document | ShadowRoot
                      ).getElementById(id)
                    : null
            if (
                controlled &&
                !isHidden(controlled, { inert: false }) &&
                !isExcluded(controlled, rules)
            ) {
                return controlled
            }
            const appeared = [...listsShown()].filter(
                (list) => !before.has(list) && !isExcluded(list, rules),
            )
            return appeared.length === 1 ? appeared[0] : null
        }
        let list = find()
        for (
            let waited = 0;
            !list?.querySelector('[role=option]') &&
            waited < LIST_WAIT_MS &&
            !signal.aborted;
            waited += ANNOUNCE_POLL_MS
        ) {
            await new Promise((resolve) =>
                setTimeout(resolve, ANNOUNCE_POLL_MS),
            )
            list = find()
        }
        return list
    }

    /**
     * The option of `list` that `value` names, as the user would pick it: the
     * one with that text, case, accents and spaces aside. `loose`, for a
     * control that takes no text, also takes the only one that starts with
     * the value, then the only one that holds it. A link is never chosen
     * here: a suggestion that leads elsewhere is the model's to click.
     */
    const choiceIn = (
        list: Element,
        value: unknown,
        { loose }: { loose: boolean },
    ): { option?: Element; matches: number } => {
        const wanted = comparable(String(value ?? ''))
        if (!wanted) {
            return { matches: 0 }
        }
        const named = [...list.querySelectorAll('[role=option]')]
            .filter(
                (option) =>
                    !isHidden(option, { inert: false }) &&
                    !isExcluded(option, rules) &&
                    option.getAttribute('aria-disabled') !== 'true',
            )
            .map((option) => ({
                option,
                name: comparable(
                    nameOf(option, rules) || option.textContent || '',
                ),
            }))
        const exact = named.filter(({ name }) => name === wanted)
        let found = exact
        if (exact.length === 0 && loose) {
            const starting = named.filter(({ name }) => name.startsWith(wanted))
            found =
                starting.length > 0
                    ? starting
                    : named.filter(({ name }) => name.includes(wanted))
        }
        const option = found.length === 1 ? found[0]?.option : undefined
        return option &&
            !option.matches('a[href]') &&
            !option.querySelector('a[href]')
            ? { option, matches: 1 }
            : { matches: found.length }
    }

    /**
     * Whether a field shows `name`, once chosen. The click must have taken
     * first: the list closed, or the option says it is selected; a list
     * still open with the option as it was means the site ignored it. Then
     * the field's value, or the box that holds it and no other control
     * (react-select's container, Radix's trigger), where sites show the
     * choice. A list still open in that box holds every option's name: there
     * the option's own state is what counts.
     */
    const fieldShows = (
        element: Element,
        option: Element,
        name: string,
        list: Element,
    ) => {
        const wanted = comparable(name)
        const listOpen = list.isConnected && !isHidden(list, { inert: false })
        const selected = option.getAttribute('aria-selected') === 'true'
        if (listOpen && option.isConnected && !selected) {
            return false
        }
        if (
            element instanceof HTMLInputElement &&
            comparable(element.value) === wanted
        ) {
            return true
        }
        let box = element
        for (let depth = 0; depth < FIELD_BOX_DEPTH; depth++) {
            const parent = box.parentElement
            if (
                !parent ||
                parent === document.body ||
                parent.querySelectorAll(FIELD_CONTROLS).length > 1
            ) {
                break
            }
            box = parent
        }
        if (listOpen && box.contains(list)) {
            return selected
        }
        return comparable(filteredText(box, rules, REGION_MAX_CHARS)).includes(
            wanted,
        )
    }

    const asksFirst = (element: Element): boolean =>
        capability.confirmEveryAction ||
        !!element.closest('[data-agent-confirm]')

    /**
     * Where the user can act: the modal dialog open on the page, when it
     * shows, else the whole body. The page is inert under the dialog, and
     * reading it would offer the agent elements its actions are refused on.
     */
    const reachable = (): { root: Element; note?: string } => {
        const modal = openModal()
        if (
            !modal ||
            isHidden(modal, { inert: false }) ||
            isExcluded(modal, rules)
        ) {
            return { root: document.body }
        }
        const name = nameOf(modal, rules)
        return {
            root: modal,
            note: `[The dialog${name ? ` "${name}"` : ''} is open: the page behind it is out of reach until it closes, so only the dialog is shown.]`,
        }
    }

    /**
     * The page after a route change, for the result: the whole body, since a
     * router may render its view outside `<main>`, or the dialog open on it.
     */
    const routedPage = () => {
        const { root, note } = reachable()
        const page = read(root, ROUTED_MAX_CHARS)
        return note ? { ...page, text: `${note}\n${page.text}` } : page
    }

    /** The fields of a form the browser refused to send, with its own message. */
    const invalidFields = (form: HTMLFormElement): string[] =>
        [...form.elements]
            .filter(
                (field): field is HTMLInputElement =>
                    'validity' in field &&
                    !(field as HTMLInputElement).validity.valid &&
                    !isExcluded(field, rules),
            )
            .slice(0, 3)
            .map(
                (field) =>
                    `${nameOf(field, rules) || roleOf(field) || 'field'}: ${field.validationMessage}`,
            )

    /**
     * Runs an action and reports its outcome (spec
     * `page-tools-improvements.md`, M3): what the page announced, where it
     * went, what the part around the element or the page routed to shows
     * now. The action runs at once; a navigation that would load another
     * document is held until the result has reached the backend, then made,
     * and the result says the page is leaving. Where the browser cannot hold
     * navigations, the callers defer the whole action as before (`leaving`).
     */
    const act = async ({
        element,
        said,
        run,
        submission,
        shows,
        signal,
    }: {
        element: Element
        /** What the result says it did, read once the action ran. */
        said: string | (() => string)
        run: () => void
        /** The call's own: a stop ends the wait for the page's answer. */
        signal?: AbortSignal
        /** The form a submit sends, and its submitter. */
        submission?: { form: HTMLFormElement; submitter: Element | null }
        /** The part of the page the element controls, when not around it. */
        shows?: () => Element | null
    }): Promise<HostToolResult | HostToolDeferredResult> => {
        const hold = holdLeaving(submission)
        const from = location.href
        const heard = listenForAnnouncements(rules)
        const scope = () => {
            const shown = shows?.()
            return shown && !isExcluded(shown, rules)
                ? shown
                : (element.closest(
                      'form, section, article, dialog, [role=dialog], main',
                  ) ??
                      element.parentElement ??
                      document.body)
        }
        const before = filteredText(scope(), rules, REGION_MAX_CHARS)
        const inDialog = !!element.closest(
            'dialog, [role=dialog], [role=alertdialog]',
        )
        const modalBefore = openModal()
        try {
            await presence?.point(element, fieldTitle(element, rules))
            if (signal) {
                stopIfAborted(signal)
            }
            run()
            await settle()
            // A save the site sends to its server is announced when the
            // answer comes back, often after the page went quiet (seen on the
            // bench: the toast came late, and the agent searched the page
            // for it)
            if (
                submission &&
                !hold?.destination &&
                invalidFields(submission.form).length === 0
            ) {
                for (
                    let waited = 0;
                    waited < ANNOUNCE_WAIT_MS &&
                    heard.said().length === 0 &&
                    !hold?.destination &&
                    !signal?.aborted &&
                    location.href === from;
                    waited += ANNOUNCE_POLL_MS
                ) {
                    await new Promise((resolve) =>
                        setTimeout(resolve, ANNOUNCE_POLL_MS),
                    )
                }
            }
        } catch (error) {
            // An action that did not happen holds nothing: the page's own
            // navigations go free again
            hold?.drop()
            heard.stop()
            throw error
        }
        // A dialog the action opened is where the user is now: its whole
        // content, not the region of the element, which may leave out its
        // buttons (seen on the bench: an editor written in, then closed
        // without saving three times)
        const modal = openModal()
        const opened =
            modal &&
            modal !== modalBefore &&
            !isHidden(modal, { inert: false }) &&
            !isExcluded(modal, rules)
                ? modal
                : undefined
        const routed = !hold?.destination && location.href !== from
        // The element went away: a dialog it was in closed, or the part of
        // the page that held it was replaced. The region around it shows
        // nothing then, and the page around is what the agent has to check
        // (the form a dialog wrote into has a save of its own)
        const gone = !element.isConnected || isHidden(element, { inert: false })
        if (routed || (!hold?.destination && gone)) {
            await pageQuiet()
        }
        const announced = heard.stop()
        const lines = [typeof said === 'function' ? said() : said]
        if (announced.length > 0) {
            lines.push(
                `The page said: ${announced.map((message) => `«${message}»`).join(' ')}`,
            )
        }
        if (hold?.destination) {
            lines.push(
                `The page is loading ${shortAddress(hold.destination)}: the conversation goes on there, so read it with page_snapshot before doing anything else.`,
            )
        } else if (routed || gone) {
            const page = routedPage()
            lines.push(
                `${routed ? 'The page is now' : 'The part of the page that held it closed or was replaced, the page is'} "${document.title}" (${location.pathname}${location.search}). Now:\n${page.text}`,
            )
            if (page.truncated) {
                lines.push('[The page goes on: page_snapshot for the rest.]')
            }
            // Seen on the bench: an editor dialog wrote into the settings
            // form, the field showed the text, and the model reported a save
            // the form's own button never made
            if (!routed && inDialog && announced.length === 0) {
                lines.push(
                    "[The dialog closed and the page announced nothing: what it held may only be written into the page's form, which its own save button sends. Do not say it is saved until the page shows it.]",
                )
            }
        } else {
            const refused = submission ? invalidFields(submission.form) : []
            if (refused.length > 0) {
                lines.push(`The form was not sent: ${refused.join('; ')}.`)
            }
            const region = opened ?? scope()
            const name = opened ? nameOf(opened, rules) : ''
            lines.push(
                `${opened ? `A dialog opened${name ? ` "${name}"` : ''}, the page behind it is out of reach until it closes. ` : ''}Now:\n${read(region, REGION_MAX_CHARS).text}`,
            )
            if (
                !opened &&
                announced.length === 0 &&
                refused.length === 0 &&
                filteredText(region, rules, REGION_MAX_CHARS) === before
            ) {
                lines.push(
                    '[No visible change yet: if the page is still working, wait for it with page_wait; if the action opened something elsewhere, read it with page_snapshot.]',
                )
            }
        }
        const result = text(lines.join('\n'))
        if (!hold) {
            return result
        }
        return {
            result,
            afterOutput: () => {
                if (hold.destination) {
                    leaveForNextPage()
                }
                hold.release()
            },
            abandon: () => hold.drop(),
        }
    }

    /**
     * The lines of the page that hold `wanted`, each with the lines around
     * it: the button of a row is often next to the text that names the row.
     */
    const find = (wanted: string) => {
        const result = read(document.body, FIND_SCAN_MAX_CHARS)
        const lines = result.text.split('\n')
        const needle = wanted.toLowerCase()
        const shown = new Set<number>()
        let matches = 0
        lines.forEach((line, at) => {
            if (line.toLowerCase().includes(needle)) {
                matches++
                for (
                    let near = Math.max(0, at - FIND_BEFORE);
                    near <= Math.min(lines.length - 1, at + FIND_AFTER);
                    near++
                ) {
                    shown.add(near)
                }
            }
        })
        const out: string[] = []
        let length = 0
        let previous = -1
        for (const at of [...shown].sort((x, y) => x - y)) {
            const line =
                (previous >= 0 && at !== previous + 1 ? '…\n' : '') + lines[at]
            if (length + line.length > FIND_MAX_CHARS) {
                out.push('[More matches: narrow the text.]')
                break
            }
            out.push(line)
            length += line.length + 1
            previous = at
        }
        return { matches, text: out.join('\n'), truncated: result.truncated }
    }

    /** A link of the page to `address`, in this tab and not excluded. */
    const linkTo = (address: string): HTMLAnchorElement | undefined => {
        let href: string
        try {
            href = new URL(address, location.href).href
        } catch {
            return undefined
        }
        return [
            ...document.querySelectorAll<HTMLAnchorElement>('a[href]'),
        ].find(
            (candidate) =>
                candidate.href === href &&
                (!candidate.target || candidate.target === '_self') &&
                !isExcluded(candidate, rules),
        )
    }

    /** An element as the user reads it, on a card or a step: a switch by its title, not its description. */
    const nameFor = (element: Element) =>
        fieldTitle(element, rules) ||
        roleOf(element) ||
        t('toolApproval.pageElement')

    /**
     * The addresses of this site the agent may open without asking (spec
     * `page-tools-improvements.md`, M6): the links of the page, hidden ones
     * in a closed menu too, and the pages the app declared.
     */
    const knownAddresses = (): Map<string, string> => {
        const known = new Map<string, string>()
        for (const page of pages()) {
            known.set(page.path, page.title ?? '')
        }
        for (const link of document.querySelectorAll<HTMLAnchorElement>(
            'a[href]',
        )) {
            const key = addressKey(link.href)
            if (
                key &&
                !known.has(key) &&
                (!link.target || link.target === '_self') &&
                !isExcluded(link, rules)
            ) {
                known.set(key, nameOf(link, rules))
            }
        }
        return known
    }
    const isKnownAddress = (address: string): boolean => {
        const key = addressKey(address)
        if (!key) {
            return false
        }
        for (const known of knownAddresses().keys()) {
            if (known === key || declaredMatches(known, key)) {
                return true
            }
        }
        return false
    }
    /** The known addresses as a list for the model, `limit` at most. */
    const addressList = (entries: [string, string][], limit: number) =>
        entries
            .slice(0, limit)
            .map(([address, name]) => `- ${address}${name ? ` (${name})` : ''}`)
            .join('\n') +
        (entries.length > limit ? `\n[and ${entries.length - limit} more]` : '')

    /** What a step says it acts on: the element's name, short (spec `page-tools-improvements.md`, M8). */
    const stepName = (element: Element | undefined) => {
        const name = element ? nameFor(element) : t('toolApproval.pageElement')
        return name.length > 40 ? `${name.slice(0, 39)}…` : name
    }

    /**
     * The fields each submit card listed, by the call's target: the submit
     * goes ahead only if the form still reads the same, since a value may
     * change while the card waits (spec `page-tools-improvements.md`, M2).
     */
    const listedForms = new Map<string, string>()
    const summarize = (form: HTMLFormElement) =>
        formSummary(form, rules, {
            checked: t('toolApproval.fieldChecked'),
            unchecked: t('toolApproval.fieldUnchecked'),
            before: firstSeen,
        })
    /** The form as the card's check compares it: every field, whole. */
    const fingerprint = (form: HTMLFormElement) =>
        JSON.stringify(
            formSummary(form, rules, {
                checked: t('toolApproval.fieldChecked'),
                whole: true,
            }),
        )
    const submitCard = (
        form: HTMLFormElement,
        question: string,
        key: string,
    ): HostToolConfirmation => {
        const summary = summarize(form)
        listedForms.set(key, fingerprint(form))
        return {
            question,
            details: summary.fields,
            unchanged: summary.unchanged,
        }
    }
    /**
     * The `page_press` calls whose Enter went to an open list, by the same
     * key as their card: they asked for none, so they never submit, even if
     * the list closed before they ran.
     */
    const listChoices = new Set<string>()
    /** Throws when the form changed since its card listed it. */
    const unchangedSinceCard = (form: HTMLFormElement, key: string) => {
        const listed = listedForms.get(key)
        listedForms.delete(key)
        if (listed !== undefined && listed !== fingerprint(form)) {
            throw new Error(
                'The form changed while the user was deciding: read it again with page_snapshot, and submit only what the user asked for.',
            )
        }
    }

    /** The element a key goes to: the one named, or the one with the focus. */
    const keyTarget = (input: RefInput): Element => {
        if (input.ref !== undefined) {
            return actionTarget(input)
        }
        let focused: Element | null = document.activeElement
        while (focused?.shadowRoot?.activeElement) {
            focused = focused.shadowRoot.activeElement
        }
        return focused ?? document.body
    }

    const policy = { timeoutMs: PAGE_TOOL_TIMEOUT_MS }
    const tools: RegisteredHostTool[] = [
        {
            name: 'page_snapshot',
            inputSchema: { type: 'object' },
            annotations: {
                title: t('messagePart.pageTool.snapshot'),
                readOnlyHint: true,
            },
            policy,
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const scope = (input as RefInput).scope
                const { root, note } =
                    scope === undefined
                        ? reachable()
                        : { root: target({ ref: scope }), note: undefined }
                const result = read(root, SNAPSHOT_MAX_CHARS)
                const declared = pages().map((page): [string, string] => [
                    page.path,
                    page.title ?? '',
                ])
                return text(
                    [
                        `Page: ${document.title} (${location.pathname})`,
                        note ?? '',
                        result.text,
                        result.truncated
                            ? '[The page goes on: call page_snapshot with the scope of a region for the rest.]'
                            : '',
                        scope === undefined && declared.length > 0
                            ? `Pages of the site (<id> is any one segment):\n${addressList(declared, KNOWN_ADDRESSES_SHOWN)}`
                            : '',
                    ]
                        .filter(Boolean)
                        .join('\n'),
                )
            },
        },
        {
            name: 'page_read',
            inputSchema: { type: 'object' },
            annotations: {
                title: t('messagePart.pageTool.read'),
                readOnlyHint: true,
            },
            policy,
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const element = target(input as RefInput)
                const result = snapshot(element, refs, rules, {
                    maxChars: SNAPSHOT_MAX_CHARS,
                    withRefs: false,
                })
                return text(result.text || '(no text)')
            },
        },
        {
            name: 'page_find',
            inputSchema: { type: 'object' },
            annotations: {
                title: t('messagePart.pageTool.find'),
                readOnlyHint: true,
            },
            policy,
            describe(input) {
                const wanted = String((input as RefInput).text ?? '').trim()
                return wanted
                    ? t('messagePart.pageStep.find', {
                          text:
                              wanted.length > 40
                                  ? `${wanted.slice(0, 39)}…`
                                  : wanted,
                      })
                    : undefined
            },
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const wanted = String((input as RefInput).text ?? '').trim()
                if (!wanted) {
                    throw new Error('Say which text to find.')
                }
                const found = find(wanted)
                return text(
                    found.matches > 0
                        ? `${found.matches} line(s) hold "${wanted}":\n${found.text}`
                        : `"${wanted}" is not on the page${found.truncated ? ' as far as it could be read' : ''}.`,
                )
            },
        },
        {
            name: 'page_wait',
            inputSchema: { type: 'object' },
            annotations: {
                title: t('messagePart.pageTool.wait'),
                readOnlyHint: true,
            },
            policy,
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const { text: raw, seconds } = input as RefInput
                const wanted = String(raw ?? '').trim()
                if (!wanted) {
                    await pageQuiet()
                    return text(
                        `The page stopped changing: "${document.title}" (${location.pathname}).`,
                    )
                }
                const limit =
                    Math.min(
                        WAIT_MAX_SECONDS,
                        Math.max(1, Number(seconds) || WAIT_MAX_SECONDS / 2),
                    ) * 1000
                const started = Date.now()
                const needle = wanted.toLowerCase()
                while (
                    !filteredText(document.body, rules, FIND_SCAN_MAX_CHARS)
                        .toLowerCase()
                        .includes(needle)
                ) {
                    if (Date.now() - started > limit) {
                        return text(
                            `"${wanted}" did not appear in ${Math.round(limit / 1000)} seconds.`,
                        )
                    }
                    await new Promise((resolve) =>
                        setTimeout(resolve, WAIT_POLL_MS),
                    )
                    stopIfAborted(signal)
                }
                const found = find(wanted)
                return text(
                    `"${wanted}" is on the page:\n${found.text || '(in a part without lines of its own)'}`,
                )
            },
        },
    ]

    if (!pageAccessAllows(capability.level, PageAccessLevel.Full)) {
        return tools
    }

    tools.push(
        {
            name: 'page_click',
            inputSchema: { type: 'object' },
            annotations: {
                title: t('messagePart.pageTool.click'),
                destructiveHint: true,
            },
            policy,
            // Asks before what the user would think twice about: a submit,
            // leaving the site, and the answer to the site's own "are you
            // sure?". A link within the site does not ask: moving around the
            // app the user has open is how the agent gets anywhere.
            confirmation(input) {
                const ref = Number((input as RefInput).ref)
                const element = refs.resolve(ref)
                // Behind an open dialog the click is refused: no card for it
                if (!element || blockingDialog(element)) {
                    return undefined
                }
                const name = nameFor(element)
                const form = formSubmittedBy(element)
                if (form) {
                    return submitCard(
                        form,
                        t('toolApproval.pageSubmit', { name }),
                        String(ref),
                    )
                }
                if (leavesSite(element)) {
                    return t('toolApproval.pageOpen', { name })
                }
                const question = confirmationQuestion(element, rules)
                if (question) {
                    return t('toolApproval.pageAnswer', { question, name })
                }
                if (!asksFirst(element)) {
                    return undefined
                }
                return leavesDocument(element)
                    ? t('toolApproval.pageOpen', { name })
                    : t('toolApproval.pageClick', { name })
            },
            describe: (input) =>
                t('messagePart.pageStep.click', {
                    name: stepName(
                        refs.resolve(Number((input as RefInput).ref)),
                    ),
                }),
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const element = actionTarget(input as RefInput)
                const line = describe(element, refs.refOf(element), rules)
                const submitted = formSubmittedBy(element)
                if (submitted) {
                    unchangedSinceCard(
                        submitted,
                        String((input as RefInput).ref),
                    )
                }
                bringIntoView(element)
                if (
                    !canHoldLeaving() &&
                    (leavesDocument(element) || submitted)
                ) {
                    return leaving(`Clicking ${line}.`, () => click(element))
                }
                // What the click opened, when the element says which part of
                // the page it controls: a menu or a dialog is often elsewhere
                const controlled = element.getAttribute('aria-controls')
                return act({
                    element,
                    said: () =>
                        `Clicked ${line}.${opensEmpty(element) ? ' Its list opened with no option yet: in a field like this the options usually come from what is written in it, so write what you look for with page_fill, then choose it with page_click. If the list is still loading, read the page again instead.' : ''}`,
                    run: () => click(element),
                    submission: submitted
                        ? { form: submitted, submitter: element }
                        : undefined,
                    shows: () =>
                        controlled ? document.getElementById(controlled) : null,
                    signal,
                })
            },
        },
        {
            name: 'page_fill',
            inputSchema: { type: 'object' },
            annotations: { title: t('messagePart.pageTool.fill') },
            policy,
            describe(input) {
                const names = fillEntries(input).map(({ ref }) =>
                    stepName(refs.resolve(Number(ref))),
                )
                return t('messagePart.pageStep.fill', {
                    name:
                        names.slice(0, 2).join(', ') +
                        (names.length > 2 ? ` +${names.length - 2}` : ''),
                })
            },
            confirmation(input) {
                const entries = fillEntries(input).map((entry) => ({
                    element: refs.resolve(Number(entry.ref)),
                    // What it writes, too: the user judges the text, not the field
                    value: String(entry.value ?? ''),
                }))
                if (
                    !entries.some(
                        ({ element }) => element && asksFirst(element),
                    )
                ) {
                    return undefined
                }
                const short = (value: string) =>
                    value.length > 120 ? `${value.slice(0, 119)}…` : value
                const nameOrElement = (element: Element | undefined) =>
                    (element && fieldTitle(element, rules)) ||
                    t('toolApproval.pageElement')
                if (entries.length === 1) {
                    return t('toolApproval.pageFill', {
                        name: nameOrElement(entries[0]?.element),
                        value: short(entries[0]?.value ?? ''),
                    })
                }
                return {
                    question: t('toolApproval.pageFillFields'),
                    details: entries.map(({ element, value }) => ({
                        label: nameOrElement(element),
                        value: short(value),
                    })),
                }
            },
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const entries = fillEntries(input)
                if (entries.length === 0 || entries.length > FILL_MAX_FIELDS) {
                    throw new Error(
                        `Give one field with ref and value, or from 1 to ${FILL_MAX_FIELDS} in fields.`,
                    )
                }
                const heard = listenForAnnouncements(rules)
                const written: string[] = []
                // Taken at the first choice: an option may lead to another
                // page, as a click does (spec `page-point-and-choose.md`, C)
                let hold: LeavingHold | undefined
                try {
                    for (const [at, entry] of entries.entries()) {
                        // A field may go away because of the one before:
                        // each ref is checked right before writing
                        let outcome = ''
                        let failure: unknown
                        let opened: Element | null = null
                        let listOnly = false
                        let matches = 0
                        // A choice the field does not show: the fields
                        // after it wait for the model to check it
                        let unconfirmed = false
                        try {
                            stopIfAborted(signal)
                            const element = actionTarget(entry)
                            bringIntoView(element)
                            await presence?.point(
                                element,
                                fieldTitle(element, rules),
                            )
                            stopIfAborted(signal)
                            const line = describe(
                                element,
                                refs.refOf(element),
                                rules,
                            )
                            listOnly = picksOnly(element)
                            if (listOnly) {
                                // No text to write: the list opens on a
                                // click, as the user opens it, unless it is
                                // open already, where a click would close it.
                                // Held from here: a trigger may lead to
                                // another page as an option may
                                hold ??= holdLeaving()
                                const before = listsShown()
                                if (
                                    element.getAttribute('aria-expanded') !==
                                    'true'
                                ) {
                                    click(element)
                                    await settle()
                                }
                                opened = await listShownBy(
                                    element,
                                    before,
                                    signal,
                                )
                                if (!opened) {
                                    throw new Error(
                                        `Clicked ${line}, and no list of options showed: read the page with page_snapshot, then choose with page_click.`,
                                    )
                                }
                                outcome = `Opened ${line}`
                            } else {
                                // The version of a text kept per language:
                                // said in so many words, as the model may
                                // overlook the mark in the line
                                const lang = / lang ([a-z]{2,3})\b/.exec(
                                    line,
                                )?.[1]
                                outcome = `${fill(element, entry.value, rules)} in ${line}${lang ? `\nThis field is the "${lang}" version of the text: if what you wrote is in another language, this is the wrong version: undo it (write back what the field held, or close its dialog without saving) and write the text in the version of its own language, whose tab or menu item is marked with it.` : ''}`
                                // The cursor on its way to the next field
                                // while the page settles: half of each
                                // field's time otherwise. Not after a field
                                // whose list may open, where the next one
                                // waits
                                const next = entries[at + 1]
                                if (
                                    next &&
                                    presence?.toward &&
                                    !opensList(element)
                                ) {
                                    headFor(next, presence.toward)
                                }
                                await settle()
                                opened = await listOpenedBy(element, signal)
                            }
                            stopIfAborted(signal)
                            const choice = opened
                                ? choiceIn(opened, entry.value, {
                                      loose: listOnly,
                                  })
                                : { matches: 0 }
                            const { option } = choice
                            matches = choice.matches
                            if (opened && option) {
                                const name =
                                    nameOf(option, rules) ||
                                    option.textContent?.trim() ||
                                    ''
                                hold ??= holdLeaving()
                                // In a list of many choices a click on one
                                // already chosen takes it away
                                const kept =
                                    opened.getAttribute(
                                        'aria-multiselectable',
                                    ) === 'true' &&
                                    option.getAttribute('aria-selected') ===
                                        'true'
                                if (!kept) {
                                    await presence?.point(option, name)
                                    stopIfAborted(signal)
                                    click(option)
                                    await settle()
                                }
                                // An option that led to another page is
                                // chosen: the page leaving is its outcome
                                unconfirmed =
                                    !kept &&
                                    !hold?.destination &&
                                    !fieldShows(element, option, name, opened)
                                // The field by its ref, role and name: its
                                // line as read before would show the value
                                // and the state it had before the choice
                                const fieldName = nameOf(element, rules)
                                const field = `[${refs.refOf(element)}] ${roleOf(element) ?? 'generic'}${fieldName ? ` ${JSON.stringify(fieldName)}` : ''}`
                                outcome = kept
                                    ? `${JSON.stringify(name)} was already chosen in ${field}`
                                    : unconfirmed
                                      ? `Clicked the option ${JSON.stringify(name)} of ${field}, and the field does not show it: check it with page_snapshot before going on.`
                                      : `Chose ${JSON.stringify(name)} in ${field}`
                                opened = null
                            }
                        } catch (error) {
                            failure = error
                        }
                        // The first field's error as it is; a later one says
                        // which fields were written before it
                        if (failure !== undefined) {
                            if (written.length === 0) {
                                throw failure
                            }
                            throw new Error(
                                `${written.join('\n')}\nStopped at [${String(entry.ref)}], the rest not written: ${failure instanceof Error ? failure.message : 'failed'}`,
                            )
                        }
                        written.push(outcome)
                        const rest = entries.slice(at + 1)
                        if (hold?.destination) {
                            written.push(
                                `The page is loading ${shortAddress(hold.destination)}: the conversation goes on there, so read it with page_snapshot before doing anything else.${rest.length > 0 ? ` Not written: ${rest.map(({ ref }) => `[${String(ref)}]`).join(', ')}.` : ''}`,
                            )
                            break
                        }
                        if (unconfirmed) {
                            if (rest.length > 0) {
                                written.push(
                                    `Not written yet: ${rest.map(({ ref }) => `[${String(ref)}]`).join(', ')}. Check this field first, then write them with page_fill.`,
                                )
                            }
                            break
                        }
                        // The text typed in a combobox is not its choice yet,
                        // and writing the next field would close the list:
                        // the site may clear the text then (seen on demoqa,
                        // spec `page-tools-improvements.md`, M10)
                        if (opened) {
                            const wanted = JSON.stringify(
                                String(entry.value ?? ''),
                            )
                            const why =
                                matches > 1
                                    ? `${matches} options match ${wanted}, so none was chosen`
                                    : listOnly
                                      ? `none of them is ${wanted}`
                                      : `no option is ${wanted} exactly`
                            written.push(
                                listOnly
                                    ? `Its list of options opened, and ${why}: if one is what you meant, choose it with page_click. Now:\n${read(opened, REGION_MAX_CHARS).text}`
                                    : `Its list of options opened, and ${why}: if one of them is what you meant, choose it with page_click. In a field like this the text alone may not count as a choice, and the site may clear it when the field loses the focus; where it does count (a search), go on without choosing. Now:\n${read(opened, REGION_MAX_CHARS).text}`,
                            )
                            if (rest.length > 0) {
                                written.push(
                                    `Not written yet, since writing another field would close the list: ${rest.map(({ ref }) => `[${String(ref)}]`).join(', ')}. Choose the option first, then write them with page_fill.`,
                                )
                            }
                            break
                        }
                    }
                } catch (error) {
                    // A call that did not finish holds nothing: the page's
                    // own navigations go free again
                    hold?.drop()
                    throw error
                } finally {
                    // A message a field shows at once: a format it refuses
                    const announced = heard.stop()
                    if (announced.length > 0) {
                        written.push(
                            `The page said: ${announced.map((message) => `«${message}»`).join(' ')}`,
                        )
                    }
                }
                const result = text(written.join('\n'))
                if (!hold) {
                    return result
                }
                const held = hold
                // Held until the answer is out, as after a click: an option
                // may lead to another page a moment after it was chosen
                return {
                    result,
                    afterOutput: () => {
                        if (held.destination) {
                            leaveForNextPage()
                        }
                        held.release()
                    },
                    abandon: () => held.drop(),
                }
            },
        },
        {
            name: 'page_scroll',
            inputSchema: { type: 'object' },
            annotations: { title: t('messagePart.pageTool.scroll') },
            policy,
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const { ref, direction } = input as RefInput
                if (ref !== undefined) {
                    bringIntoView(target({ ref }))
                } else {
                    window.scrollBy({
                        top:
                            (direction === 'up' ? -1 : 1) *
                            window.innerHeight *
                            0.8,
                    })
                }
                await settle()
                return text('Scrolled.')
            },
        },
        {
            name: 'page_hover',
            inputSchema: { type: 'object' },
            annotations: { title: t('messagePart.pageTool.hover') },
            policy,
            describe: (input) =>
                t('messagePart.pageStep.hover', {
                    name: stepName(
                        refs.resolve(Number((input as RefInput).ref)),
                    ),
                }),
            confirmation(input) {
                const element = refs.resolve(Number((input as RefInput).ref))
                return element && asksFirst(element)
                    ? t('toolApproval.pageHover', { name: nameFor(element) })
                    : undefined
            },
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const element = actionTarget(input as RefInput)
                bringIntoView(element)
                hover(element)
                await settle()
                const controlled = element.getAttribute('aria-controls')
                const opened = controlled
                    ? document.getElementById(controlled)
                    : null
                return text(
                    `Hovered ${describe(element, refs.refOf(element), rules)}. Now:\n${opened && !isExcluded(opened, rules) ? read(opened, REGION_MAX_CHARS).text : around(element)}\n[If something opened elsewhere on the page, read it with page_snapshot.]`,
                )
            },
        },
        {
            name: 'page_press',
            inputSchema: { type: 'object' },
            annotations: { title: t('messagePart.pageTool.press') },
            policy,
            confirmation(input) {
                const { key } = input as RefInput
                let element: Element
                try {
                    element = keyTarget(input as RefInput)
                } catch {
                    return undefined
                }
                const cardKey = `press:${String((input as RefInput).ref)}`
                // Enter in an open list picks its option: the site takes the
                // key and sends nothing, and a card would ask to send the
                // form (seen on demoqa, spec `page-tools-improvements.md`, M10)
                const choosing = key === 'Enter' && !!openListOf(element, rules)
                if (choosing) {
                    listChoices.add(cardKey)
                } else {
                    listChoices.delete(cardKey)
                }
                const form =
                    key === 'Enter' && !choosing
                        ? formSubmittedByEnter(element)
                        : null
                if (form) {
                    return submitCard(
                        form,
                        t('toolApproval.pageSubmit', {
                            name: nameFor(element),
                        }),
                        cardKey,
                    )
                }
                // A key that may answer the site's "are you sure?": its
                // buttons take Enter and Space, its handler any key it likes
                const question =
                    key === 'Enter' || key === 'Space'
                        ? confirmationQuestion(element, rules, {
                              anyElement: true,
                          })
                        : undefined
                if (question) {
                    return t('toolApproval.pageAnswer', {
                        question,
                        name: `${String(key)} · ${nameFor(element)}`,
                    })
                }
                return asksFirst(element)
                    ? t('toolApproval.pagePress', {
                          key: String(key),
                          name: nameFor(element),
                      })
                    : undefined
            },
            describe(input) {
                const { key, ref } = input as RefInput
                return t('messagePart.pageStep.press', {
                    key: String(key),
                    name:
                        ref === undefined
                            ? t('toolApproval.pageFocused')
                            : stepName(refs.resolve(Number(ref))),
                })
            },
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                const key = String((input as RefInput).key)
                if (!(PAGE_KEYS as readonly string[]).includes(key)) {
                    throw new Error(
                        `No key "${key}": the keys are ${PAGE_KEYS.join(', ')}.`,
                    )
                }
                const element = keyTarget(input as RefInput)
                if (isExcluded(element, rules)) {
                    throw new Error(
                        'That element is off limits: the site excluded it.',
                    )
                }
                if ((input as RefInput).ref !== undefined) {
                    ;(element as HTMLElement).focus?.()
                }
                const line =
                    element === document.body
                        ? 'the page'
                        : describe(element, refs.refOf(element), rules)
                const cardKey = `press:${String((input as RefInput).ref)}`
                const choosing =
                    key === 'Enter' &&
                    (listChoices.delete(cardKey) ||
                        !!openListOf(element, rules))
                const form =
                    key === 'Enter' && !choosing
                        ? formSubmittedByEnter(element)
                        : null
                if (form) {
                    unchangedSinceCard(form, cardKey)
                    const submit = () => {
                        if (!press(element, key)) {
                            submitByEnter(form)
                        }
                    }
                    if (!canHoldLeaving()) {
                        return leaving(`Pressing Enter in ${line}.`, submit)
                    }
                    return act({
                        element,
                        said: `Pressed Enter in ${line}.`,
                        run: submit,
                        submission: {
                            form,
                            submitter: defaultButtonOf(form) ?? null,
                        },
                        signal,
                    })
                }
                // Enter in an open list asked for no card: the form stays
                // unsent whatever the page's own handler of the key does
                const sendable = choosing ? formSubmittedByEnter(element) : null
                const held = sendable ? holdSubmits(sendable) : undefined
                let handled = true
                try {
                    return await act({
                        element,
                        said: () =>
                            held?.stopped
                                ? `Pressed Enter on ${line}: the page tried to send the form with it, and it was stopped, since sending needs the user's approval. Choose the option with page_click; to send the form, use its submit button.`
                                : sendable && !handled
                                  ? `Pressed Enter on ${line}: the page did not take it, and the form was not sent. With its list open, Enter here only picks an option: choose it with page_click. To send the form, use its submit button.`
                                  : `Pressed ${key} on ${line}.`,
                        run: () => {
                            handled = press(element, key)
                            if (!handled && key === 'Escape') {
                                cancelDialogOf(element)
                            }
                        },
                        shows: () =>
                            element === document.body ? document.body : null,
                        signal,
                    })
                } finally {
                    held?.release()
                }
            },
        },
        {
            name: 'page_back',
            inputSchema: { type: 'object' },
            annotations: { title: t('messagePart.pageTool.back') },
            policy,
            // The entry before may belong to another site: only the
            // Navigation API can tell, listing this origin's entries alone.
            // Where it cannot, going back asks like leaving the site
            confirmation: () =>
                capability.confirmEveryAction || !previousEntryIsOwn()
                    ? t('toolApproval.pageBack')
                    : undefined,
            async execute(_input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                if (history.length <= 1) {
                    throw new Error(
                        'There is no page before this one in this tab.',
                    )
                }
                return leaving('Going back to the previous page.', () =>
                    history.back(),
                )
            },
        },
        {
            name: 'page_navigate',
            inputSchema: { type: 'object' },
            annotations: { title: t('messagePart.pageTool.navigate') },
            policy,
            describe(input) {
                try {
                    const url = new URL(
                        String((input as RefInput).url),
                        location.href,
                    )
                    return t('messagePart.pageStep.navigate', {
                        url: `${url.pathname}${url.search}`,
                    })
                } catch {
                    return undefined
                }
            },
            confirmation(input) {
                const url = String((input as RefInput).url ?? '')
                const link = linkTo(url)
                if (
                    capability.confirmEveryAction ||
                    (link && asksFirst(link))
                ) {
                    return t('toolApproval.pageNavigate', { url })
                }
                // An address of this site the page does not show: perhaps one
                // the model made up from the names of the menus
                if (!link && addressKey(url) && !isKnownAddress(url)) {
                    const known = [...knownAddresses().entries()]
                    return {
                        question: t('toolApproval.pageNavigateUnknown', {
                            url: shortAddress(url),
                        }),
                        refusal: `The user did not let you open ${shortAddress(url)}, an address the page does not show. Open one of these instead, or click through the site's menus:\n${addressList(known, KNOWN_ADDRESSES_SHOWN)}`,
                    }
                }
                return undefined
            },
            async execute(input, { signal }) {
                await pageChangeSettled()
                stopIfAborted(signal)
                let url: URL
                try {
                    url = new URL(
                        String((input as RefInput).url),
                        location.href,
                    )
                } catch {
                    throw new Error('That is not an address.')
                }
                if (url.origin !== location.origin) {
                    throw new Error(
                        `Only addresses of this site (${location.origin}): to go elsewhere, the user opens the page.`,
                    )
                }
                // A link of the page to the same address goes through the
                // site's own router, which keeps the app as it is; without
                // one, the router is asked the way a link would (routeTo),
                // and only an address no router takes loads the page
                const address = `${url.pathname}${url.search}`
                const link = linkTo(url.href)
                if (link) {
                    return canHoldLeaving()
                        ? act({
                              element: link,
                              said: `Opened ${address} through its link.`,
                              run: () => click(link),
                              signal,
                          })
                        : leaving(`Opening ${address}.`, () => click(link))
                }
                // A router that answers with the load of another document
                // (a redirect from its popstate handler) is held as a
                // click's would be, until the result has reached the backend
                const hold = holdLeaving()
                let routed: boolean
                try {
                    routed = await routeTo(url.href)
                } catch (error) {
                    hold?.drop()
                    throw error
                }
                if (hold?.destination) {
                    const destination = hold.destination
                    return {
                        result: text(
                            `Opening ${address}: the page is loading ${shortAddress(destination)}, the conversation goes on there, so read it with page_snapshot before doing anything else.`,
                        ),
                        afterOutput: () => {
                            leaveForNextPage()
                            hold.release()
                        },
                        abandon: () => hold.drop(),
                    }
                }
                hold?.drop()
                if (routed) {
                    const page = routedPage()
                    return text(
                        `Opened ${address}: the page is now "${document.title}". Now:\n${page.text}${page.truncated ? '\n[The page goes on: page_snapshot for the rest.]' : ''}`,
                    )
                }
                return leaving(`Opening ${address}.`, () =>
                    location.replace(url.href),
                )
            },
        },
    )
    return tools
}
